import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ClipboardCheck, Calendar, IndianRupee, Clapperboard, Camera, FileCheck,
  CheckCircle2, XCircle, Star, ExternalLink, MessageSquare, Flag, CheckCheck,
  Megaphone, Target, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import SidebarLayout from '../components/SidebarLayout';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { hasLevel } from '../utils/permissions';

import API from '../utils/api';

const TABS = [
  { key: 'all', label: 'All', icon: ClipboardCheck, color: 'text-indigo-400' },
  { key: 'leave', label: 'Leaves', icon: Calendar, color: 'text-purple-400' },
  { key: 'reimbursement', label: 'Reimbursements', icon: IndianRupee, color: 'text-amber-400' },
  { key: 'production_completion', label: 'Production', icon: Clapperboard, color: 'text-cyan-400' },
  { key: 'execution_completion', label: 'Execution', icon: Megaphone, color: 'text-fuchsia-400' },
  { key: 'performance_completion', label: 'Performance', icon: Target, color: 'text-blue-400' },
  { key: 'timesheet_daily', label: 'Timesheets', icon: Clock, color: 'text-teal-400' },
  { key: 'deliverable_completion', label: 'Deliverables', icon: FileCheck, color: 'text-emerald-400' },
  { key: 'shoot_completion', label: 'Shoots', icon: Camera, color: 'text-rose-400' },
];

// Types that use the Clear / Flag workflow (not Approve / Reject)
const CLEAR_FLAG_TYPES = new Set([
  'production_completion',
  'execution_completion',
  'performance_completion',
  'deliverable_completion',
  'shoot_completion',
  'timesheet_daily',
]);

const STATUS_OPTIONS = ['Pending', 'Flagged', 'Cleared', 'Approved', 'Rejected', 'all'];

export default function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [actionDialog, setActionDialog] = useState(null);
  const { user } = useAuth();
  const canEdit = hasLevel(user, 'approvals', 'edit');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (tab !== 'all') params.approval_type = tab;

      const [appRes, statsRes] = await Promise.all([
        axios.get(`${API}/approvals`, { withCredentials: true, params }),
        axios.get(`${API}/approvals/stats`, { withCredentials: true }),
      ]);
      setApprovals(appRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [tab, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (id, action, notes) => {
    try {
      await axios.post(`${API}/approvals/${id}/action`, { action, notes }, { withCredentials: true });
      const msg = {
        approve: 'Approved', reject: 'Rejected',
        clear: 'Cleared', flag: 'Flagged', close: 'Resolved & Cleared',
      }[action] || 'Updated';
      toast.success(msg);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSpecial = async (taskId) => {
    try {
      await axios.put(`${API}/production/tasks/${taskId}/special`, {}, { withCredentials: true });
      toast.success('Special production toggled');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <SidebarLayout title="Approvals" subtitle="Review and manage pending approvals">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="approval-kpis">
        <div className="kpi-card border-l-4 border-indigo-500/40">
          <span className="text-[10px] font-semibold tracking-widest uppercase text-indigo-300">Pending</span>
          <div className="font-heading text-2xl font-black text-white mt-1">{stats?.total_pending || 0}</div>
        </div>
        <div className="kpi-card border-l-4 border-amber-500/40">
          <span className="text-[10px] font-semibold tracking-widest uppercase text-amber-300">Flagged</span>
          <div className="font-heading text-2xl font-black text-white mt-1">{stats?.flagged || 0}</div>
        </div>
        {['production_completion', 'execution_completion', 'performance_completion'].map(key => {
          const cfg = TABS.find(t => t.key === key);
          return (
            <div key={key} className="kpi-card border-l-4 border-white/5">
              <span className={`text-[10px] font-semibold tracking-widest uppercase ${cfg?.color}`}>{cfg?.label}</span>
              <div className="font-heading text-2xl font-black text-white mt-1">{stats?.by_type?.[key] || 0}</div>
            </div>
          );
        })}
      </div>

      {/* Tab Nav */}
      <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = t.key === 'all' ? stats?.total_pending : stats?.by_type?.[t.key];
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold tracking-wide transition-colors whitespace-nowrap ${tab === t.key ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white border border-white/10'}`}
              data-testid={`approval-tab-${t.key}`}
            >
              <Icon size={14} />
              {t.label}
              {count > 0 && <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full ml-1">{count}</span>}
            </button>
          );
        })}
        <div className="flex-1" />
        <div className="flex bg-black/40 rounded-lg border border-white/10 p-0.5 overflow-x-auto">
          {STATUS_OPTIONS.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap ${statusFilter === s ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid={`status-filter-${s}`}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Approvals List */}
      <div className="space-y-2" data-testid="approvals-list">
        {loading && approvals.length === 0 ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : approvals.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <ClipboardCheck size={32} className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No {statusFilter !== 'all' ? statusFilter.toLowerCase() : ''} approvals{tab !== 'all' ? ` in ${TABS.find(t => t.key === tab)?.label}` : ''}</p>
          </div>
        ) : approvals.map(a => (
          <ApprovalCard
            key={a.id}
            approval={a}
            canEdit={canEdit}
            onSpecial={handleSpecial}
            onOpenDialog={setActionDialog}
          />
        ))}
      </div>

      {/* Action Dialog with Notes */}
      {actionDialog && (
        <ActionDialog
          approval={actionDialog.approval}
          action={actionDialog.action}
          onClose={() => setActionDialog(null)}
          onConfirm={(notes) => {
            handleAction(actionDialog.approval.id, actionDialog.action, notes);
            setActionDialog(null);
          }}
        />
      )}
    </SidebarLayout>
  );
}

function StatusPill({ status }) {
  const cfg = {
    Pending: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    Approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Cleared: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Rejected: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    Flagged: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  }[status] || 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  return <span className={`text-[10px] px-2 py-0.5 rounded border ${cfg}`}>{status}</span>;
}

function ApprovalCard({ approval, canEdit, onSpecial, onOpenDialog }) {
  const typeConfig = {
    reimbursement: { icon: IndianRupee, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/15' },
    leave: { icon: Calendar, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/15' },
    production_completion: { icon: Clapperboard, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/15' },
    execution_completion: { icon: Megaphone, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10 border-fuchsia-500/15' },
    performance_completion: { icon: Target, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/15' },
    timesheet_daily: { icon: Clock, color: 'text-teal-400', bg: 'bg-teal-500/10 border-teal-500/15' },
    deliverable_completion: { icon: FileCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/15' },
    shoot_completion: { icon: Camera, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/15' },
  };

  const config = typeConfig[approval.type] || typeConfig.production_completion;
  const Icon = config.icon;
  const status = approval.status;
  const isClearFlag = CLEAR_FLAG_TYPES.has(approval.type);
  const data = approval.data || {};

  const borderClass =
    status === 'Pending' ? 'border-white/5'
    : status === 'Flagged' ? 'border-amber-500/20'
    : status === 'Approved' || status === 'Cleared' ? 'border-emerald-500/10'
    : 'border-rose-500/10';

  return (
    <div className={`glass-card p-4 border ${borderClass}`} data-testid={`approval-card-${approval.id}`}>
      <div className="flex items-start gap-4">
        {/* Type Badge */}
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${config.bg}`}>
          <Icon size={16} className={config.color} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-semibold tracking-widest uppercase ${config.color}`}>
              {approval.type?.replace(/_/g, ' ')}
            </span>
            <StatusPill status={status} />
            {approval.type === 'execution_completion' && data.on_time === false && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400">Delayed</span>
            )}
            {approval.type === 'performance_completion' && data.on_time === false && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400">Delayed</span>
            )}
          </div>
          <p className="text-sm text-white font-medium mb-1 truncate">{approval.title}</p>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span>by {approval.submitted_by}</span>
            <span>{new Date(approval.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
          </div>

          {/* Context Data */}
          <div className="mt-2 flex flex-wrap gap-2">
            {approval.type === 'reimbursement' && (
              <>
                <Tag label="Amount" value={`INR ${data.amount?.toLocaleString()}`} />
                <Tag label="Category" value={data.category} />
                {data.receipt_link && <LinkTag href={data.receipt_link}>Receipt</LinkTag>}
              </>
            )}
            {approval.type === 'leave' && (
              <>
                <Tag label="Type" value={data.leave_type} />
                <Tag label="Days" value={data.days} />
                <Tag label="From" value={data.start_date} />
                <Tag label="To" value={data.end_date} />
                {data.reason && <Tag label="Reason" value={data.reason} />}
              </>
            )}
            {approval.type === 'production_completion' && (
              <>
                <Tag label="Type" value={data.task_type} />
                <Tag label="Client" value={data.client_name} />
                <Tag label="Duration" value={data.duration} />
                {data.final_drive_link && <LinkTag href={data.final_drive_link}>Final Output</LinkTag>}
              </>
            )}
            {approval.type === 'execution_completion' && (
              <>
                <Tag label="Type" value={data.task_type} />
                <Tag label="Client" value={data.client_name} />
                <Tag label="Assigned" value={data.assigned_name} />
                <Tag label="Delivery" value={data.delivery_date} />
                {data.supporting_links?.slice(0, 3).map((link, i) => (
                  <LinkTag key={i} href={link}>Link {i + 1}</LinkTag>
                ))}
              </>
            )}
            {approval.type === 'performance_completion' && (
              <>
                <Tag label="Type" value={data.task_type} />
                <Tag label="Client" value={data.client_name} />
                <Tag label="Assigned" value={data.assigned_name} />
                <Tag label="Delivery" value={data.delivery_date} />
                {data.supporting_links?.slice(0, 3).map((link, i) => (
                  <LinkTag key={i} href={link}>Link {i + 1}</LinkTag>
                ))}
              </>
            )}
            {approval.type === 'timesheet_daily' && (
              <>
                <Tag label="Board" value={data.board} />
                <Tag label="Date" value={data.date} />
                <Tag label="Total" value={`${data.total_hours}h ${data.total_minutes}m`} />
                <Tag label="Entries" value={data.entries_count} />
              </>
            )}
            {approval.type === 'shoot_completion' && (
              <>
                <Tag label="Type" value={data.shoot_type} />
                <Tag label="Client" value={data.client_name} />
                <Tag label="Location" value={data.location} />
                <Tag label="Date" value={data.shoot_date} />
                {data.raw_footage_link && <LinkTag href={data.raw_footage_link}>Raw Footage</LinkTag>}
              </>
            )}
            {approval.type === 'deliverable_completion' && (
              <>
                <Tag label="Type" value={data.deliverable_type} />
                <Tag label="Category" value={data.deliverable_category} />
                <Tag label="Client" value={data.client_name} />
                <Tag label="Completed" value={`${data.completed_count}/${data.total_quantity}`} />
              </>
            )}
          </div>

          {/* Timesheet drilldown */}
          {approval.type === 'timesheet_daily' && data.entries?.length > 0 && (
            <div className="mt-3 border-t border-white/5 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                <span>Entries</span>
                {data.by_category?.length > 0 && (
                  <div className="flex gap-1">
                    {data.by_category.map(c => (
                      <span key={c.category} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400">
                        {c.category}: <span className="text-white font-mono">{c.hours}h {c.minutes}m</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {data.entries.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="text-slate-500 w-6">{i + 1}.</span>
                    <span className="text-cyan-400 font-mono w-14">{e.hours}h {e.minutes}m</span>
                    <span className="text-slate-400 shrink-0">[{e.category}]</span>
                    {e.client_name && <span className="text-slate-500">· {e.client_name}</span>}
                    <span className="text-slate-300 truncate">{e.task_description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes if already processed */}
          {approval.notes && (
            <div className="mt-2 text-[11px] text-slate-400 flex items-start gap-1">
              <MessageSquare size={11} className="shrink-0 mt-0.5" />
              <span>{approval.notes}</span>
            </div>
          )}

          {approval.processed_by && (
            <div className="mt-1 text-[10px] text-slate-600">
              {status} by {approval.processed_by}{approval.processed_at ? ` · ${new Date(approval.processed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Production special 2x toggle (only for pending production_completion) */}
          {canEdit && status === 'Pending' && approval.type === 'production_completion' && data.task_id && (
            <button
              onClick={() => onSpecial(data.task_id)}
              className="p-2 rounded-lg bg-amber-500/5 hover:bg-amber-500/15 text-slate-500 hover:text-amber-400 transition-colors border border-transparent hover:border-amber-500/20"
              title="Mark as Special Production (2x)"
              data-testid={`special-${approval.id}`}
            >
              <Star size={15} />
            </button>
          )}

          {/* Leaves / Reimbursements: Approve / Reject */}
          {canEdit && !isClearFlag && status === 'Pending' && (
            <>
              <button
                onClick={() => onOpenDialog({ approval, action: 'approve' })}
                className="p-2 rounded-lg bg-emerald-500/5 hover:bg-emerald-500/15 text-emerald-400 transition-colors border border-transparent hover:border-emerald-500/20"
                data-testid={`approve-${approval.id}`}
                title="Approve"
              >
                <CheckCircle2 size={15} />
              </button>
              <button
                onClick={() => onOpenDialog({ approval, action: 'reject' })}
                className="p-2 rounded-lg bg-rose-500/5 hover:bg-rose-500/15 text-rose-400 transition-colors border border-transparent hover:border-rose-500/20"
                data-testid={`reject-${approval.id}`}
                title="Reject"
              >
                <XCircle size={15} />
              </button>
            </>
          )}

          {/* Tasks / Timesheets: Clear / Flag (when Pending) */}
          {canEdit && isClearFlag && status === 'Pending' && (
            <>
              <button
                onClick={() => onOpenDialog({ approval, action: 'clear' })}
                className="p-2 rounded-lg bg-emerald-500/5 hover:bg-emerald-500/15 text-emerald-400 transition-colors border border-transparent hover:border-emerald-500/20"
                data-testid={`clear-${approval.id}`}
                title="Clear"
              >
                <CheckCircle2 size={15} />
              </button>
              <button
                onClick={() => onOpenDialog({ approval, action: 'flag' })}
                className="p-2 rounded-lg bg-amber-500/5 hover:bg-amber-500/15 text-amber-400 transition-colors border border-transparent hover:border-amber-500/20"
                data-testid={`flag-${approval.id}`}
                title="Flag for review"
              >
                <Flag size={15} />
              </button>
            </>
          )}

          {/* Flagged items: Close to mark resolved */}
          {canEdit && isClearFlag && status === 'Flagged' && (
            <button
              onClick={() => onOpenDialog({ approval, action: 'close' })}
              className="px-3 py-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors border border-emerald-500/20 text-[11px] font-semibold flex items-center gap-1"
              data-testid={`close-${approval.id}`}
              title="Mark resolved"
            >
              <CheckCheck size={13} /> Resolve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Tag({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <span className="text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-slate-400">
      <span className="text-slate-600">{label}: </span>{value}
    </span>
  );
}

function LinkTag({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[10px] text-cyan-400 flex items-center gap-0.5 hover:text-cyan-300">
      <ExternalLink size={10} /> {children}
    </a>
  );
}

function ActionDialog({ approval, action, onClose, onConfirm }) {
  const [notes, setNotes] = useState('');
  const cfg = {
    approve: { title: 'Approve', color: 'text-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-700', placeholder: 'Good work!' },
    clear:   { title: 'Clear',   color: 'text-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-700', placeholder: 'Looks good' },
    close:   { title: 'Resolve', color: 'text-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-700', placeholder: 'Issue resolved — clearing' },
    flag:    { title: 'Flag',    color: 'text-amber-400',   btn: 'bg-amber-600 hover:bg-amber-700',     placeholder: 'Reason for flagging — needs manual review' },
    reject:  { title: 'Reject',  color: 'text-rose-400',    btn: 'bg-rose-600 hover:bg-rose-700',       placeholder: 'Reason for rejection...' },
  }[action] || { title: action, color: 'text-white', btn: 'bg-indigo-600', placeholder: 'Notes' };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-[#0a0a0f] border-white/10">
        <DialogHeader>
          <DialogTitle className={`font-heading ${cfg.color}`}>
            {cfg.title} — {approval.type?.replace(/_/g, ' ')}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <p className="text-sm text-white mb-3">{approval.title}</p>
          <label className="text-xs text-slate-500 block mb-1">Notes {action === 'flag' ? '*' : '(optional)'}</label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={cfg.placeholder}
            className="bg-black/40 border-white/10 text-white h-9 text-sm"
            data-testid="approval-notes"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button
            onClick={() => onConfirm(notes)}
            disabled={action === 'flag' && !notes.trim()}
            className={cfg.btn}
            data-testid="confirm-approval-action"
          >
            {cfg.title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
